import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useInventory } from '../context/InventoryContext';

const { FiArrowLeft, FiCheck, FiChevronRight, FiDollarSign, FiCalendar, FiSearch } = FiIcons;

const ModelDetail = () => {
  const { id } = useParams();
  const { getModelData, filteredVehicles } = useInventory();
  const [activeTab, setActiveTab] = useState('overview');

  const allModels = getModelData();
  const model = allModels.find(m => m.id === id);

  // Get inventory vehicles matching this model
  const availableVehicles = filteredVehicles.filter(v => v.model.toLowerCase() === model?.name.toLowerCase());

  if (!model) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-700 mb-4">Model Not Found</h1>
          <Link to="/models" className="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700 transition-colors">
            Browse Models
          </Link>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'features', label: 'Features & Specs' },
    { id: 'gallery', label: 'Gallery' },
    { id: 'inventory', label: `Available ${model.name} Models` },
  ];

  const colors = [
    { name: 'Mythos Black', hex: '#000000' },
    { name: 'Glacier White', hex: '#FFFFFF' },
    { name: 'Daytona Gray', hex: '#636363' },
    { name: 'Navarra Blue', hex: '#1C3E6C' },
    { name: 'Tango Red', hex: '#AF1E2D' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-black text-white">
        <div className="absolute inset-0 z-0">
          <img src={model.image} alt={`Audi ${model.name}`} className="w-full h-full object-cover opacity-70" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <Link to="/models" className="inline-flex items-center text-white hover:text-gray-200 mb-6">
            <SafeIcon icon={FiArrowLeft} className="w-5 h-5 mr-2" />
            <span>Back to Models</span>
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center md:text-left md:max-w-xl"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-4">
              Audi {model.name}
            </h1>
            <p className="text-xl md:text-2xl mb-6 text-gray-200">
              Starting at ${model.startingPrice.toLocaleString()}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
              <Link to={`/inventory?model=${model.name}`} className="bg-red-600 text-white px-8 py-3 rounded-md font-semibold hover:bg-red-700 transition-colors duration-200">
                Shop {model.name} Inventory
              </Link>
              <Link to={`/build-your-audi/${model.id}`} className="bg-white text-black px-8 py-3 rounded-md font-semibold hover:bg-gray-200 transition-colors duration-200">
                Build Your {model.name}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tabs Navigation */}
      <section className="bg-white shadow-md sticky top-16 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto py-4 space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`whitespace-nowrap px-1 py-2 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-red-600 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Overview Tab Content */}
      {activeTab === 'overview' && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  {model.name} Overview
                </h2>
                <p className="text-lg text-gray-600 mb-8">
                  {model.description}
                </p>
                <div className="space-y-6 mb-8">
                  {model.highlights.map((highlight, index) => (
                    <div key={index} className="flex items-center space-x-4">
                      <div className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center text-white font-bold">
                        {highlight.value.split(' ')[0]}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{highlight.title}</h3>
                        <p className="text-gray-600">{highlight.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to={`/build-your-audi/${model.id}`} className="bg-black text-white px-8 py-3 rounded-md font-semibold hover:bg-gray-900 transition-colors duration-200">
                    Build Your {model.name}
                  </Link>
                  <Link to="/contact" className="border border-black text-black px-8 py-3 rounded-md font-semibold hover:bg-gray-100 transition-colors duration-200">
                    Request a Quote
                  </Link>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <img src={model.image} alt={`Audi ${model.name}`} className="rounded-lg shadow-lg" />
              </motion.div>
            </div>

            {/* Key Features */}
            <div className="mt-16">
              <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
                Key Features
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {model.features.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="bg-gray-50 p-6 rounded-lg"
                  >
                    <div className="flex items-start space-x-3">
                      <SafeIcon icon={FiCheck} className="w-5 h-5 text-red-600 mt-1" />
                      <p className="text-gray-800">{feature}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Features & Specs Tab Content */}
      {activeTab === 'features' && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Features & Specifications
            </h2>

            {/* Specs */}
            <div className="mb-16">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Technical Specifications
              </h3>
              <div className="bg-gray-50 rounded-lg overflow-hidden">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
                  <div className="p-6 border-b md:border-b-0 md:border-r border-gray-200">
                    <h4 className="text-sm text-gray-500 mb-1">Engine</h4>
                    <p className="text-lg font-semibold">
                      {model.id === 'e-tron' ? 'Electric Dual Motor' : (model.id.includes('s5') ? '3.0L TFSI V6' : '2.0L TFSI 4-Cylinder')}
                    </p>
                  </div>
                  <div className="p-6 border-b md:border-b-0 md:border-r border-gray-200">
                    <h4 className="text-sm text-gray-500 mb-1">Horsepower</h4>
                    <p className="text-lg font-semibold">
                      {model.id === 'e-tron' ? '402 HP (boost)' : (model.id.includes('s5') ? '349 HP' : model.id === 'q7' ? '335 HP' : '261 HP')}
                    </p>
                  </div>
                  <div className="p-6 border-b lg:border-b-0 lg:border-r border-gray-200">
                    <h4 className="text-sm text-gray-500 mb-1">Drivetrain</h4>
                    <p className="text-lg font-semibold">
                      {model.id === 'e-tron' ? 'Quattro Electric AWD' : 'Quattro AWD'}
                    </p>
                  </div>
                  <div className="p-6">
                    <h4 className="text-sm text-gray-500 mb-1">Acceleration</h4>
                    <p className="text-lg font-semibold">
                      {model.id === 'e-tron' ? '0-100 km/h in 5.7s' : (model.id.includes('s5') ? '0-100 km/h in 4.5s' : '0-100 km/h in 5.8s')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Dimensions */}
            <div className="mb-16">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Dimensions
              </h3>
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <h4 className="text-sm text-gray-500 mb-1">Length</h4>
                    <p className="text-lg font-semibold">
                      {model.category === 'SUV' ? (model.name === 'Q3' ? '4,484 mm' : model.name === 'Q5' ? '4,682 mm' : '5,052 mm') : (model.id === 'e-tron' ? '4,901 mm' : '4,762 mm')}
                    </p>
                  </div>
                  <div>
                    <h4 className="text-sm text-gray-500 mb-1">Width</h4>
                    <p className="text-lg font-semibold">
                      {model.category === 'SUV' ? (model.name === 'Q3' ? '1,849 mm' : model.name === 'Q5' ? '1,893 mm' : '1,968 mm') : (model.id === 'e-tron' ? '1,935 mm' : '1,847 mm')}
                    </p>
                  </div>
                  <div>
                    <h4 className="text-sm text-gray-500 mb-1">Height</h4>
                    <p className="text-lg font-semibold">
                      {model.category === 'SUV' ? (model.name === 'Q3' ? '1,598 mm' : model.name === 'Q5' ? '1,662 mm' : '1,741 mm') : (model.id === 'e-tron' ? '1,629 mm' : '1,433 mm')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Available Colors */}
            <div className="mb-16">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Available Colors
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                {colors.map((color, index) => (
                  <div key={index} className="text-center">
                    <div
                      className="w-16 h-16 mx-auto rounded-full border border-gray-300"
                      style={{ backgroundColor: color.hex }}
                    ></div>
                    <p className="mt-2 text-sm">{color.name}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Packages */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Available Packages
              </h3>
              <div className="space-y-6">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">Premium Package</h4>
                  <p className="text-gray-600 mb-4">Enhance your driving experience with premium features.</p>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <li className="flex items-center space-x-2">
                      <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600" />
                      <span>Bang & Olufsen 3D Sound System</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600" />
                      <span>Panoramic sunroof</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600" />
                      <span>Heated steering wheel</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600" />
                      <span>Wireless phone charging</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">Technology Package</h4>
                  <p className="text-gray-600 mb-4">Advanced technology features for connectivity and convenience.</p>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <li className="flex items-center space-x-2">
                      <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600" />
                      <span>Audi virtual cockpit plus</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600" />
                      <span>MMI Navigation plus</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600" />
                      <span>Head-up display</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600" />
                      <span>Audi phone box</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">S line Sport Package</h4>
                  <p className="text-gray-600 mb-4">Enhanced styling and sport-focused features.</p>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <li className="flex items-center space-x-2">
                      <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600" />
                      <span>S line exterior styling</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600" />
                      <span>Sport suspension</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600" />
                      <span>Sport seats</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600" />
                      <span>19" or 20" wheels (model dependent)</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Gallery Tab Content */}
      {activeTab === 'gallery' && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Audi {model.name} Gallery
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* We'll use the model image plus some generic Audi images */}
              <img
                src={model.image}
                alt={`Audi ${model.name} exterior`}
                className="w-full h-64 object-cover rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer"
              />
              <img
                src="https://images.unsplash.com/photo-1542282088-72c9c27ed0cd?w=800"
                alt={`Audi ${model.name} interior`}
                className="w-full h-64 object-cover rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer"
              />
              <img
                src="https://images.unsplash.com/photo-1543854589-fdd815246367?w=800"
                alt={`Audi ${model.name} dashboard`}
                className="w-full h-64 object-cover rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer"
              />
              <img
                src="https://images.unsplash.com/photo-1544381272-1a1b509c22ab?w=800"
                alt={`Audi ${model.name} wheels`}
                className="w-full h-64 object-cover rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer"
              />
              <img
                src="https://images.unsplash.com/photo-1549740093-e87191a0dd27?w=800"
                alt={`Audi ${model.name} rear view`}
                className="w-full h-64 object-cover rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer"
              />
              <img
                src="https://images.unsplash.com/photo-1606220588771-b3f7c2a4f880?w=800"
                alt={`Audi ${model.name} driving`}
                className="w-full h-64 object-cover rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer"
              />
            </div>
          </div>
        </section>
      )}

      {/* Inventory Tab Content */}
      {activeTab === 'inventory' && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Available Audi {model.name} Models
            </h2>
            {availableVehicles.length > 0 ? (
              <div className="space-y-6">
                {availableVehicles.map((vehicle) => (
                  <motion.div
                    key={vehicle.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white border border-gray-200 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
                  >
                    <div className="flex flex-col md:flex-row">
                      <div className="md:w-1/3">
                        <img
                          src={vehicle.images[0]}
                          alt={`${vehicle.year} Audi ${vehicle.model}`}
                          className="w-full h-48 md:h-full object-cover"
                        />
                      </div>
                      <div className="md:w-2/3 p-6">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="text-xl font-bold text-gray-900 mb-1">
                              {vehicle.year} Audi {vehicle.model}
                            </h3>
                            <p className="text-gray-600">{vehicle.exterior} • Stock #{vehicle.stock}</p>
                          </div>
                          <div className="text-xl font-bold text-red-600">
                            ${vehicle.price.toLocaleString()}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 my-4">
                          <div>
                            <p className="text-xs text-gray-500">Mileage</p>
                            <p className="font-medium">{vehicle.mileage.toLocaleString()} km</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Engine</p>
                            <p className="font-medium">{vehicle.engine}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Transmission</p>
                            <p className="font-medium">{vehicle.transmission}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Fuel Type</p>
                            <p className="font-medium">{vehicle.fuelType}</p>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 mb-4">
                          {vehicle.features.slice(0, 3).map((feature, i) => (
                            <span key={i} className="bg-gray-100 px-2 py-1 text-xs rounded">
                              {feature}
                            </span>
                          ))}
                          {vehicle.features.length > 3 && (
                            <span className="text-gray-500 text-xs px-2 py-1">
                              +{vehicle.features.length - 3} more
                            </span>
                          )}
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3">
                          <Link
                            to={`/vehicle/${vehicle.id}`}
                            className="bg-red-600 text-white px-6 py-2 rounded-md font-medium hover:bg-red-700 transition-colors text-center flex-1 flex items-center justify-center space-x-1"
                          >
                            <SafeIcon icon={FiSearch} className="w-4 h-4" />
                            <span>View Details</span>
                          </Link>
                          <Link
                            to={`/contact?vehicle=${vehicle.id}`}
                            className="bg-white border border-black text-black px-6 py-2 rounded-md font-medium hover:bg-gray-100 transition-colors text-center flex-1 flex items-center justify-center space-x-1"
                          >
                            <SafeIcon icon={FiCalendar} className="w-4 h-4" />
                            <span>Schedule Test Drive</span>
                          </Link>
                          <Link
                            to={`/financing?vehicle=${vehicle.id}`}
                            className="bg-white border border-black text-black px-6 py-2 rounded-md font-medium hover:bg-gray-100 transition-colors text-center flex-1 flex items-center justify-center space-x-1"
                          >
                            <SafeIcon icon={FiDollarSign} className="w-4 h-4" />
                            <span>Calculate Payment</span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <p className="text-gray-600 mb-4">
                  No {model.name} models currently in stock. Contact us to inquire about ordering or upcoming inventory.
                </p>
                <Link
                  to={`/build-your-audi/${model.id}`}
                  className="bg-red-600 text-white px-8 py-3 rounded-md font-semibold hover:bg-red-700 transition-colors duration-200 inline-flex items-center space-x-2"
                >
                  <span>Build Your Own {model.name}</span>
                  <SafeIcon icon={FiChevronRight} className="w-5 h-5" />
                </Link>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Related Models */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            Explore Related Models
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {allModels
              .filter(m => m.id !== model.id && m.category === model.category)
              .slice(0, 3)
              .map((relatedModel, index) => (
                <motion.div
                  key={relatedModel.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
                >
                  <img src={relatedModel.image} alt={`Audi ${relatedModel.name}`} className="w-full h-48 object-cover" />
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      Audi {relatedModel.name}
                    </h3>
                    <p className="text-red-600 font-semibold mb-4">
                      Starting at ${relatedModel.startingPrice.toLocaleString()}
                    </p>
                    <div className="flex space-x-2">
                      <Link to={`/models/${relatedModel.id}`} className="flex-1 bg-black text-white py-3 px-4 rounded-md font-medium hover:bg-gray-900 transition-colors text-center">
                        Learn More
                      </Link>
                      <Link to={`/build-your-audi/${relatedModel.id}`} className="flex-1 bg-red-600 text-white py-3 px-4 rounded-md font-medium hover:bg-red-700 transition-colors text-center">
                        Build Your Own
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Experience the Audi {model.name}?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Visit our Halifax showroom for a test drive or speak with our Audi specialists to learn more about the {model.name}.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={`/build-your-audi/${model.id}`} className="bg-red-600 text-white px-8 py-3 rounded-md font-semibold hover:bg-red-700 transition-colors duration-200">
                Build Your {model.name}
              </Link>
              <Link to="/contact" className="border-2 border-white text-white px-8 py-3 rounded-md font-semibold hover:bg-white hover:text-black transition-colors duration-200">
                Schedule Test Drive
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ModelDetail;